/* =================================================
   HOMEPAGE HERO VIDEO — THE ONE CONTROLLER
   Added Sep 2026 (Eli). Split out of assets/global.js so the
   hero's code ships with the hero section rather than on every
   page, and so loading can begin during the HTML parse instead
   of after a deferred script.

   TWO HALVES; THIS IS THE SECOND.
   1. A tiny inline script in sections/video.liquid, directly
      after the hero's <deferred-media>. It runs the instant the
      browser parses past the hero, picks this device's variant
      and starts that ONE video downloading. Inline on purpose:
      an external file costs a round trip, and being early is
      the whole point.
   2. This file (deferred): releases the cover image once the
      video genuinely has a frame, removes the other device's
      hero section, and owns the sound from then on.

   The inline script marks each hero with eliHeroReady, the same
   flag the OLD hero block at the end of assets/global.js checks
   before doing anything — so that block is now inert and should
   be deleted from global.js.

   SOUND — one state model; nothing else in the theme touches it:
     on arrival      muted
     tap / click     toggles, but only while it is on screen
     scrolled away   fades out over ~0.22s, then truly muted
     tab hidden      muted at once (no fade: rAF is frozen while
                     hidden, so a ramp would never finish)
     scrolled back   stays muted until the next deliberate tap
   OFF SCREEN IS NEVER AUDIBLE. The fade is only polish: coming
   back mid-fade does not cancel it, and nothing restores sound
   on its own.
   TO RETUNE: VISIBLE_ENOUGH is how much of the hero must be on
   screen for sound to be allowed; FADE_MS is the fade length.
   ================================================= */
(function () {
  if (window.eliHeroController) return; // the section may include this twice
  window.eliHeroController = true;

  var VISIBLE_ENOUGH = 0.3; // 30% of the hero on screen = sound allowed
  var FADE_MS = 220;
  var HERO_MEDIA = '[data-hero-variant="desktop"] deferred-media, [data-hero-variant="mobile"] deferred-media';

  function wantsMobileHero() {
    return !!(window.matchMedia && window.matchMedia('(hover: none) and (pointer: coarse)').matches);
  }

  // The unused variant is only hidden by CSS before this runs, so it has
  // never loaded anything; this takes it out of the page for good.
  function removeUnusedHeroVariant() {
    var unused = wantsMobileHero() ? 'desktop' : 'mobile';
    var sections = document.querySelectorAll('.video-section[data-hero-variant="' + unused + '"]');
    Array.prototype.forEach.call(sections, function (section) {
      section.remove();
    });
  }

  function controlHero(media) {
    if (media.eliHeroControlled) return;
    media.eliHeroControlled = true;
    media.eliHeroReady = true; // keeps global.js's old block out even if the inline script did not run

    // Fallback only: normally the inline script inserted and started this
    // video long before now.
    if (!media.querySelector('video') && typeof media.loadContent === 'function') {
      media.classList.add('hero-media-loading');
      media.loadContent(false);
    }

    var video = media.querySelector('video');
    if (!video) {
      media.classList.remove('hero-media-loading');
      return;
    }

    video.muted = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.loop = true;
    video.play().catch(function () {});

    /* ---- Cover image: hand over on the video's first real frame ----
       readyState 2 (HAVE_CURRENT_DATA) is the earliest point at which a
       frame provably exists, so the cover never gives way to a black box,
       and we never wait for the later buffering states either. */
    function showVideoWhenReady() {
      if (video.readyState < 2) return;
      media.classList.remove('hero-media-loading');
      video.removeEventListener('loadeddata', showVideoWhenReady);
      video.removeEventListener('playing', showVideoWhenReady);
      video.removeEventListener('timeupdate', showVideoWhenReady);
    }
    video.addEventListener('loadeddata', showVideoWhenReady);
    video.addEventListener('playing', showVideoWhenReady);
    video.addEventListener('timeupdate', showVideoWhenReady);
    showVideoWhenReady(); // it may already be ready from cache

    /* ---- Sound ---- */
    var onScreen = true;
    var fadeFrame = null;
    var NORMAL_VOLUME = 1;

    // How much of the video is on screen, as a share of itself (or of the
    // screen, when the video is larger than the screen).
    function visibleShare() {
      var box = video.getBoundingClientRect();
      var screenHeight = window.innerHeight || document.documentElement.clientHeight;
      var screenWidth = window.innerWidth || document.documentElement.clientWidth;
      var height = Math.min(box.bottom, screenHeight) - Math.max(box.top, 0);
      var width = Math.min(box.right, screenWidth) - Math.max(box.left, 0);
      if (height <= 0 || width <= 0) return 0;
      var compareWith = Math.min(box.width * box.height, screenWidth * screenHeight);
      return (height * width) / Math.max(1, compareWith);
    }

    function cancelFade() {
      if (fadeFrame === null) return;
      cancelAnimationFrame(fadeFrame);
      fadeFrame = null;
    }

    // The real, final silence. volume goes back up while muted so the next
    // deliberate unmute starts at full level, not at the fade's end.
    function muteNow() {
      cancelFade();
      if (!video.muted) {
        video.muted = true;
        video.setAttribute('muted', '');
      }
      video.volume = NORMAL_VOLUME;
      describeSound();
    }

    // One rAF ramp, not a chain of timeouts, and only ever one at a time.
    // Whatever happens during it, it can only end in muteNow().
    function fadeToMute() {
      if (video.muted) return muteNow();
      if (fadeFrame !== null) return; // already on its way to silence
      var from = video.volume;
      var started = null;
      fadeFrame = requestAnimationFrame(function step(now) {
        if (started === null) started = now;
        var progress = Math.min(1, (now - started) / FADE_MS);
        video.volume = Math.max(0, from * (1 - progress));
        if (progress < 1) {
          fadeFrame = requestAnimationFrame(step);
          return;
        }
        fadeFrame = null;
        muteNow();
      });
    }

    // The only place sound is ever turned on.
    function toggleSound() {
      if (!onScreen) return muteNow();
      cancelFade();
      if (video.muted) {
        video.volume = NORMAL_VOLUME;
        video.muted = false;
        video.removeAttribute('muted');
      } else {
        video.muted = true;
        video.setAttribute('muted', '');
      }
      describeSound();
      video.play().catch(function () {});
    }

    function describeSound() {
      video.setAttribute('aria-label', video.muted ? 'Play the sound of this video' : 'Mute this video');
      video.setAttribute('aria-pressed', video.muted ? 'false' : 'true');
    }

    function checkOnScreen() {
      onScreen = !document.hidden && visibleShare() >= VISIBLE_ENOUGH;
      if (onScreen) return;
      // Hidden tab: mute outright, since the ramp's frames would never run.
      if (document.hidden) return muteNow();
      fadeToMute();
    }

    video.setAttribute('tabindex', '0');
    video.setAttribute('role', 'button');
    describeSound();

    // One listener for the whole hero, so a tap on the video or on the cover
    // image while it is still loading both count.
    (media.closest('.video-section') || media).addEventListener('click', toggleSound);
    video.addEventListener('keydown', function (event) {
      if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
      event.preventDefault();
      toggleSound();
    });

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(checkOnScreen, { threshold: [0, 0.15, VISIBLE_ENOUGH, 0.6] }).observe(video);
    }
    // Belt and braces: the observer covers scrolling, these cover resizing,
    // rotating, tab switching, and anything else that could otherwise leave
    // sound playing from a hero nobody can see.
    window.addEventListener('scroll', checkOnScreen, { passive: true });
    window.addEventListener('resize', checkOnScreen, { passive: true });
    document.addEventListener('visibilitychange', checkOnScreen);
    // Safety net: if anything else ever unmutes this while it is off screen,
    // cut it at once rather than fading (our own ramp is skipped here).
    video.addEventListener('volumechange', function () {
      if (fadeFrame !== null) return;
      if (!video.muted && !onScreen) muteNow();
    });

    checkOnScreen();
  }

  function setUpHeroVideos() {
    removeUnusedHeroVariant();
    Array.prototype.forEach.call(document.querySelectorAll(HERO_MEDIA), controlHero);
  }

  setUpHeroVideos();
  // Again after the theme editor re-renders a section
  document.addEventListener('shopify:section:load', setUpHeroVideos);
})();
